import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { API_BASE_URL } from '../../config/api';
import { authHeaders } from '../../services/auth';
import DataTable from '../../components/ui/DataTable';
import Spinner from '../../components/ui/Spinner';
import ToastProvider, { useToast } from '../../components/ui/ToastProvider';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Modal from '../../components/ui/Modal';
import { adminTheme } from '../../theme-admin';
import { cache } from '../../utils/cache';

// Page Layout
const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${adminTheme.spacing.xl};
  animation: slideIn 0.3s ease-out;
`;

const Title = styled.h2`
  font-family: ${adminTheme.fonts.heading};
  font-size: ${adminTheme.fontSizes['3xl']};
  font-weight: ${adminTheme.fontWeights.bold};
  color: ${adminTheme.colors.gray[900]};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${adminTheme.spacing.md};
`;

const CreateButton = styled.button`
  padding: ${adminTheme.spacing.md} ${adminTheme.spacing.xl};
  background: ${adminTheme.gradients.purple};
  color: white;
  border: none;
  border-radius: ${adminTheme.borderRadius.lg};
  font-weight: ${adminTheme.fontWeights.bold};
  font-size: ${adminTheme.fontSizes.base};
  cursor: pointer;
  transition: ${adminTheme.transitions.fast};
  box-shadow: ${adminTheme.shadows.md};
  display: flex;
  align-items: center;
  gap: ${adminTheme.spacing.sm};

  &:hover {
    filter: brightness(1.1);
    transform: translateY(-2px);
    box-shadow: ${adminTheme.shadows.lg};
  }
`;

const TableWrapper = styled.div`
  background: ${adminTheme.colors.background.primary};
  border: 1px solid ${adminTheme.colors.gray[200]};
  border-radius: ${adminTheme.borderRadius['2xl']};
  padding: ${adminTheme.spacing.xl};
  box-shadow: ${adminTheme.shadows.md};
`;

const ActionButton = styled.button`
  padding: ${adminTheme.spacing.sm} ${adminTheme.spacing.md};
  border-radius: ${adminTheme.borderRadius.lg};
  font-weight: ${adminTheme.fontWeights.semibold};
  font-size: ${adminTheme.fontSizes.sm};
  cursor: pointer;
  transition: ${adminTheme.transitions.fast};
  border: 1px solid ${adminTheme.colors.gray[300]};
  background: ${adminTheme.colors.background.primary};
  color: ${adminTheme.colors.gray[700]};

  &:hover {
    background: ${adminTheme.colors.gray[100]};
    transform: translateY(-1px);
  }
`;

const DeleteButton = styled(ActionButton)`
  border-color: ${adminTheme.colors.danger[300]};
  background: ${adminTheme.colors.danger[500]};
  color: white;

  &:hover {
    background: ${adminTheme.colors.danger[600]};
  }
`;

const ModalForm = styled.div`
  display: grid;
  gap: ${adminTheme.spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${adminTheme.spacing.sm};
`;

const Label = styled.label`
  font-size: ${adminTheme.fontSizes.sm};
  font-weight: ${adminTheme.fontWeights.semibold};
  color: ${adminTheme.colors.gray[700]};
`;

const Input = styled.input`
  padding: ${adminTheme.spacing.md};
  border: 1px solid ${adminTheme.colors.gray[300]};
  border-radius: ${adminTheme.borderRadius.lg};
  font-size: ${adminTheme.fontSizes.base};
  transition: ${adminTheme.transitions.fast};
  font-family: ${adminTheme.fonts.primary};
  color: ${adminTheme.colors.gray[900]};
  background-color: ${adminTheme.colors.background.primary};

  &:focus {
    outline: none;
    border-color: ${adminTheme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${adminTheme.colors.primary[100]};
  }

  &:disabled {
    background: ${adminTheme.colors.gray[100]};
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${adminTheme.colors.gray[400]};
  }
`;

const Select = styled.select`
  padding: ${adminTheme.spacing.md};
  border: 1px solid ${adminTheme.colors.gray[300]};
  border-radius: ${adminTheme.borderRadius.lg};
  color: ${adminTheme.colors.gray[900]};
  background-color: ${adminTheme.colors.background.primary};
  font-size: ${adminTheme.fontSizes.base};
  transition: ${adminTheme.transitions.fast};
  font-family: ${adminTheme.fonts.primary};
  cursor: pointer;
  background: white;

  &:focus {
    outline: none;
    border-color: ${adminTheme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${adminTheme.colors.primary[100]};
  }
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${adminTheme.spacing.md};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const RoleBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: ${adminTheme.borderRadius.full};
  font-size: ${adminTheme.fontSizes.xs};
  font-weight: ${adminTheme.fontWeights.semibold};
  background: ${props => props.role === 'admin' ? adminTheme.colors.primary[100] : adminTheme.colors.gray[200]};
  color: ${props => props.role === 'admin' ? adminTheme.colors.primary[700] : adminTheme.colors.gray[700]};
`;

const InfoNote = styled.small`
  color: ${adminTheme.colors.gray[600]};
  font-size: ${adminTheme.fontSizes.xs};
  margin-top: -${adminTheme.spacing.xs};
`;

function PageBody() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const toast = useToast();

  const getInitialForm = () => ({
    email: '',
    username: '',
    password: '',
    role: 'author',
    firstName: '',
    lastName: '',
    isActive: true
  });

  const [editForm, setEditForm] = useState(getInitialForm());

  const load = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh) {
      const cached = cache.get('users-list-admin');
      if (cached) {
        setItems(cached);
        return;
      }
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/users?includeAll=true`, { headers: authHeaders() });
      const data = await res.json();
      let users = [];
      // Handle different response formats
      if (data.data?.users && Array.isArray(data.data.users)) {
        users = data.data.users;
      } else if (Array.isArray(data.data)) {
        users = data.data;
      } else if (Array.isArray(data)) {
        users = data;
      }
      setItems(users);
      cache.set('users-list-admin', users);
    } catch (error) {
      toast.error('Failed to load users');
      setItems([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setIsCreating(true);
    setEditing(null);
    setEditForm(getInitialForm());
  };

  const openEdit = (user) => {
    setIsCreating(false);
    setEditing(user);
    setEditForm({
      email: user.email || '',
      username: user.username || '',
      password: '', // Don't populate password for security
      role: user.role || 'author',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      isActive: user.isActive !== false
    });
  };

  const closeModal = () => {
    setIsCreating(false);
    setEditing(null);
    setEditForm(getInitialForm());
  };

  const saveUser = async () => {
    try {
      // Validation
      if (!editForm.email || !editForm.username) {
        toast.error('Email and username are required');
        return;
      }

      if (isCreating && !editForm.password) {
        toast.error('Password is required for new users');
        return;
      }

      const url = isCreating
        ? `${API_BASE_URL}/users`
        : `${API_BASE_URL}/users/${editing._id}`;
      
      const method = isCreating ? 'POST' : 'PUT';

      // Don't send password if empty (for edits)
      const payload = { ...editForm };
      if (!isCreating && !payload.password) {
        delete payload.password;
      }

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Operation failed');
      }

      toast.success(isCreating ? 'User created! 🎉' : 'User updated! ✨');
      closeModal();
      cache.invalidate('users-list');
      load(true);
    } catch (error) {
      toast.error(error.message || 'Failed to save user');
    }
  };

  const onDelete = async (id) => {
    setConfirmId(id);
  };

  const confirmDelete = async () => {
    const id = confirmId;
    setConfirmId(null);
    try {
      const res = await fetch(`${API_BASE_URL}/users/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (!res.ok) throw new Error('delete failed');
      toast.success('User deleted');
      cache.invalidate('users-list');
      load(true);
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  return (
    <div>
      <PageHeader>
        <Title>
          <span>👥</span>
          User Management
        </Title>
        <CreateButton onClick={openCreate}>
          <span>➕</span>
          Create New User
        </CreateButton>
      </PageHeader>

      <TableWrapper>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <Spinner label="Loading users..." />
          </div>
        ) : (
          <DataTable
            columns={[
              { key: 'email', header: 'Email', width: '1.5fr' },
              { key: 'username', header: 'Username', width: '1fr' },
              { 
                key: 'name', 
                header: 'Name', 
                width: '1fr',
                render: (u) => {
                  const name = [u.firstName, u.lastName].filter(Boolean).join(' ');
                  return name || '-';
                }
              },
              { 
                key: 'role', 
                header: 'Role', 
                width: '0.8fr',
                render: (u) => <RoleBadge role={u.role}>{u.role || 'author'}</RoleBadge>
              },
              {
                key: 'createdAt',
                header: 'Joined',
                width: '0.8fr',
                render: (u) => u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '-'
              },
            ]}
            rows={items}
            actions={(u) => (
              <>
                <ActionButton onClick={() => openEdit(u)}>✏️ Edit</ActionButton>
                <DeleteButton onClick={() => onDelete(u._id)}>🗑️ Delete</DeleteButton>
              </>
            )}
          />
        )}
      </TableWrapper>

      <ConfirmDialog
        open={!!confirmId}
        onCancel={() => setConfirmId(null)}
        onConfirm={confirmDelete}
        title="Delete user?"
        description="This action cannot be undone. The user will be permanently deleted."
      />

      <Modal
        open={isCreating || !!editing}
        title={isCreating ? '✨ Create New User' : '✏️ Edit User'}
        onCancel={closeModal}
        onSave={saveUser}
        saveLabel={isCreating ? 'Create User' : 'Save Changes'}
      >
        <ModalForm>
          <TwoColumn>
            <FormGroup>
              <Label>Email *</Label>
              <Input
                type="email"
                placeholder="user@example.com"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                disabled={!isCreating} // Email cannot be changed after creation
                required
              />
              {!isCreating && <InfoNote>Email cannot be changed</InfoNote>}
            </FormGroup>

            <FormGroup>
              <Label>Username *</Label>
              <Input
                placeholder="johndoe"
                value={editForm.username}
                onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                required
              />
            </FormGroup>
          </TwoColumn>

          <TwoColumn>
            <FormGroup>
              <Label>First Name</Label>
              <Input
                placeholder="John"
                value={editForm.firstName}
                onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>Last Name</Label>
              <Input
                placeholder="Doe"
                value={editForm.lastName}
                onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
              />
            </FormGroup>
          </TwoColumn>

          <FormGroup>
            <Label>Password {isCreating ? '*' : '(leave blank to keep current)'}</Label>
            <Input
              type="password"
              placeholder={isCreating ? "Enter password" : "Leave blank to keep current password"}
              value={editForm.password}
              onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
              required={isCreating}
            />
            {!isCreating && <InfoNote>Only fill this if you want to change the password</InfoNote>}
          </FormGroup>

          <FormGroup>
            <Label>Role</Label>
            <Select
              value={editForm.role}
              onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
            >
              <option value="author">Author</option>
              <option value="admin">Admin</option>
            </Select>
            <InfoNote>Admin users have full access to all features</InfoNote>
          </FormGroup>
        </ModalForm>
      </Modal>
    </div>
  );
}

export default function AdminUsers() {
  return (
    <ToastProvider>
      <PageBody />
    </ToastProvider>
  );
}
